import { authenticatedFetch } from './authApi';
import { getCachedOrFetch, invalidateCachedValue } from './requestCache';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

export class ProfilesApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ProfilesApiError';
  }
}

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const PROFILES_TTL_MS = 30_000;

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const errorValue = (payload as UnknownRecord).error;
    if (typeof errorValue === 'string' && errorValue.trim().length > 0) return errorValue;
    if (errorValue && typeof errorValue === 'object' && 'message' in (errorValue as UnknownRecord)) {
      const message = (errorValue as UnknownRecord).message;
      if (typeof message === 'string' && message.trim().length > 0) return message;
    }
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const performProfilesFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  return await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
  });
};

const profilesRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const response = await performProfilesFetch(path, options);
  const payload = await parseJson(response);

  if (!response.ok) {
    throw new ProfilesApiError(
      extractMessage(payload, `Profiles API request failed (${response.status})`),
      response.status,
    );
  }

  return extractData(payload);
};

export const getMyProfileApi = async (): Promise<unknown | null> => {
  return await getCachedOrFetch('profiles:me', PROFILES_TTL_MS, async () => {
    const payload = await profilesRequest('/api/v1/profiles/me', { method: 'GET' });
    return payload ?? null;
  });
};

export const updateMyProfileApi = async (payload: Record<string, unknown>): Promise<unknown | null> => {
  const data = await profilesRequest('/api/v1/profiles/me', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue('profiles:me');
  invalidateCachedValue('profiles:list:');
  return data ?? null;
};

export const listProfilesApi = async (params?: {
  fields?: string;
  q?: string;
  role?: string;
  club_id?: string;
  limit?: number;
  offset?: number;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (params?.fields) query.set('fields', params.fields);
  if (params?.q) query.set('q', params.q);
  if (params?.role) query.set('role', params.role);
  if (params?.club_id) query.set('club_id', params.club_id);
  if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
  if (typeof params?.offset === 'number') query.set('offset', String(params.offset));

  const qs = query.toString();
  return await getCachedOrFetch(`profiles:list:${qs}`, PROFILES_TTL_MS, async () => {
    const payload = await profilesRequest(`/api/v1/profiles${qs ? `?${qs}` : ''}`, { method: 'GET' });
    return Array.isArray(payload) ? payload : [];
  });
};

export const getProfileApi = async (profileId: string): Promise<unknown | null> => {
  const payload = await profilesRequest(`/api/v1/profiles/${encodeURIComponent(profileId)}`, { method: 'GET' });
  return payload ?? null;
};

export const updateProfileApi = async (profileId: string, payload: Record<string, unknown>): Promise<unknown | null> => {
  const data = await profilesRequest(`/api/v1/profiles/${encodeURIComponent(profileId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue('profiles:me');
  invalidateCachedValue('profiles:list:');
  return data ?? null;
};

export const batchProfilesApi = async (ids: string[]): Promise<unknown[]> => {
  const payload = await profilesRequest('/api/v1/profiles/batch', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  return Array.isArray(payload) ? payload : [];
};

export const getLatestProfileCreatedAtApi = async (): Promise<string | null> => {
  const payload = await profilesRequest('/api/v1/profiles/stats/latest-created-at', { method: 'GET' });
  if (!payload || typeof payload !== 'object') return null;
  const latest = (payload as UnknownRecord).latest_created_at;
  return typeof latest === 'string' ? latest : null;
};

export const createUserApi = async (payload: {
  email: string;
  displayName: string;
  role: 'admin' | 'club-manager' | 'user';
  language: string;
  club_id?: string;
}): Promise<void> => {
  await profilesRequest('/api/v1/admin/users', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue('profiles:list:');
};

export const checkUserEmailAvailability = async (email: string): Promise<boolean> => {
  const result = await profilesRequest('/api/v1/admin/users/email-availability', {
    method: 'POST', cache: 'no-store', body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
  if (!result || typeof result !== 'object' || !('available' in result) || typeof result.available !== 'boolean') {
    throw new Error('Invalid email availability response');
  }
  return result.available;
};

export type ClubUserRemovalPreview = { user_id: string; can_delete: boolean; blocked_reason: 'linked_data' | null; linked_teams?: { name: string; season: string }[]; linked_content?: { kind: string; count: number }[]; linked_roles?: string[]; has_other_links?: boolean; linked_other_content?: { kind: string; count: number }[] };
export const getClubUserRemovalPreview = async (userId: string, signal: AbortSignal): Promise<ClubUserRemovalPreview> => {
  const result = await profilesRequest(`/api/v1/profiles/club-users/${encodeURIComponent(userId)}/deletion-preview`, {signal, cache: 'no-store'});
  const preview = result as ClubUserRemovalPreview | null;
  if (!preview || preview.user_id !== userId || typeof preview.can_delete !== 'boolean') throw new Error('Invalid removal preview');
  return preview;
};
export const deleteClubUserApi = async (userId: string): Promise<void> => {
  const result = await profilesRequest(`/api/v1/profiles/club-users/${encodeURIComponent(userId)}`, {method: 'DELETE'});
  if ((result as {user_id?: string; status?: string} | null)?.user_id !== userId || (result as {status?: string}).status !== 'deleted') throw new Error('Removal not confirmed');
  invalidateCachedValue('profiles:list:');
};
